// Bridges shell-level events to ESA domain state.
// Mounted via EsaShellEffectsBridge in Layout so it stays active even when the
// ESA panel is collapsed.
import { useEffect } from 'react';
import type { SurfaceSelectionContext as EsaBusSelectionContext } from '@growthaxl/web-foundation';
import { shellEventBus } from '../../../shell/events/eventBus';
import { esaBusManager } from '../esaBus';
import { getMessageCache } from '../cache/messageCacheInstance';
import { useEsaDomainStore } from '../store/esaDomainStore';
import { useShellStore } from '../../../shell/store/shellStore';
import type { SurfaceSelectionContext } from '../store/esaDomainStore';
import { formatSupersetSelectionMessage } from '../utils/supersetSelectionMessage';
import { showHilRaisedToast } from '../../../shell/toasts';
import { isAgOpsHilRequestToast } from '../../../shell/toasts/resolveHilToastDedupeKeys';

const toDomainSurfaceContext = (
  ctx: EsaBusSelectionContext,
  existing: SurfaceSelectionContext | null
): SurfaceSelectionContext => ({
  ...existing,
  surfaceId: ctx.surfaceId,
  surfaceType: ctx.surfaceType,
  selection: ctx.selection,
  ...ctx.selection,
});

const applySurfaceSelection = (
  domainCtx: SurfaceSelectionContext,
  source: 'shell-event-bus' | 'esa-bus-manager'
): void => {
  if (process.env.NODE_ENV !== 'production') {
    const selectionMessage = formatSupersetSelectionMessage(domainCtx);
    console.log(`[ESA host] surface selection received via ${source}`, {
      surfaceId: domainCtx.surfaceId,
      surfaceType: domainCtx.surfaceType,
      selection: domainCtx.selection,
      supersetFilter: domainCtx.supersetFilter,
      selectionMessage,
    });
  }

  const mergedContext: SurfaceSelectionContext = {
    ...(useEsaDomainStore.getState().lastSurfaceContext ?? {}),
    ...domainCtx,
  };

  useEsaDomainStore.getState().setSurfaceContext(mergedContext);
};

export function useEsaShellEffects(): void {
  const setSurfaceContext = useEsaDomainStore(s => s.setSurfaceContext);
  const setEsaPanelMode = useShellStore(s => s.setEsaPanelMode);
  const addInboxNotification = useEsaDomainStore(s => s.addInboxNotification) as
    ((n: import('../types').InboxNotification) => void) | undefined;
  const addPendingHITLFn = useEsaDomainStore(s => s.addPendingHITL) as
    ((hitl: Parameters<ReturnType<typeof useEsaDomainStore.getState>['addPendingHITL']>[0]) => void) | undefined;
  const setUnreadRequests = useEsaDomainStore(s => s.setUnreadRequests);
  const setUnreadNotifications = useEsaDomainStore(s => s.setUnreadNotifications);
  const setInboxRequestsStale = useEsaDomainStore(s => s.setInboxRequestsStale);

  useEffect(() => {
    if (process.env.NODE_ENV !== 'production') {
      console.log('[ESA host] EsaShellEffectsBridge subscribed to event bus');
    }

    // SOLUTION_CONTEXT_CHANGED — update surface context when solution changes
    const offContext = shellEventBus.on('SOLUTION_CONTEXT_CHANGED', e => {
      const extended = e.payload as {
        solutionId?: string;
        workspaceId?: string;
        canvasId?: string;
      };
      const current = useEsaDomainStore.getState().lastSurfaceContext;
      setSurfaceContext({
        ...(current ?? {}),
        solutionId: extended.solutionId,
        workspaceId: extended.workspaceId,
        canvasId: extended.canvasId,
      });
    });

    // Surface -> ESA via shell event bus (MF-safe, same path as ESA_SEND_MESSAGE)
    const offSurfaceSelection = shellEventBus.on('ESA_SURFACE_SELECTION_CHANGED', e => {
      applySurfaceSelection(
        toDomainSurfaceContext(
          {
            surfaceId: e.payload.surfaceId,
            surfaceType: e.payload.surfaceType,
            selection: e.payload.selection,
          },
          useEsaDomainStore.getState().lastSurfaceContext
        ),
        'shell-event-bus'
      );
    });

    // Surface -> ESA via ESABus.sendContext() (in-process fallback)
    const offEsaContext = esaBusManager.onContext(ctx => {
      applySurfaceSelection(
        toDomainSurfaceContext(ctx, useEsaDomainStore.getState().lastSurfaceContext),
        'esa-bus-manager'
      );
    });

    const offOpen = shellEventBus.on('ESA_OPEN_PANEL', () => {
      setEsaPanelMode('briefing');
    });

    const syncShellNavigationContext = (): void => {
      const shell = useShellStore.getState();
      const current = useEsaDomainStore.getState().lastSurfaceContext;
      const hasCanvasNavigation = Boolean(
        shell.activeSolution ||
          shell.activeCanvas ||
          shell.activeWorkspace ||
          current?.solutionId ||
          current?.canvasId ||
          current?.workspaceId ||
          current?.surfaceId
      );

      setSurfaceContext({
        ...(current ?? {}),
        solutionId: shell.activeSolution ?? current?.solutionId,
        canvasId: shell.activeCanvas ?? current?.canvasId,
        workspaceId: shell.activeWorkspace ?? current?.workspaceId,
        activeCapability:
          shell.activeCapability ??
          current?.activeCapability ??
          (hasCanvasNavigation ? 'dashboard' : undefined),
      });
    };

    syncShellNavigationContext();

    const offShellNavigation = useShellStore.subscribe((state, prev) => {
      if (
        state.activeSolution === prev.activeSolution &&
        state.activeCanvas === prev.activeCanvas &&
        state.activeWorkspace === prev.activeWorkspace &&
        state.activeCapability === prev.activeCapability
      ) {
        return;
      }
      syncShellNavigationContext();
    });

    // AGENT_APPROVAL_REQUESTED — HIL escalation via shell event bus
    // 1. Add to pendingHITL (regression guard)
    // 2. Add inbox notification (new)
    // 3. Mark inbox stale so it re-fetches (new)
    const offApproval = shellEventBus.on('AGENT_APPROVAL_REQUESTED', e => {
      const extPayload = e.payload as {
        requestId: string;
        agentId: string;
        hilId?: string;
        conversationId?: string;
        taskId?: string;
        title?: string;
        summary?: string;
      };
      const pendingId = extPayload.hilId || extPayload.requestId;
      addPendingHITLFn?.({
        id: pendingId,
        agentRunId: extPayload.agentId,
        agentUkey: '',
        question: 'Approval required',
        requestType: null,
        priority: 'NORMAL',
        status: 'PENDING',
        conversationId: extPayload.conversationId ?? '',
        createdAt: new Date().toISOString(),
      });
      addInboxNotification?.({
        id: `notif-${pendingId}`,
        urgency: 'high',
        kind: 'alert',
        title: extPayload.title ?? 'New approval requested',
        body: extPayload.summary ?? `Agent ${extPayload.agentId} requires approval`,
        solution: '',
        time: new Date().toISOString(),
        read: false,
      });
      // setInboxLoading is intentionally NOT called here — setInboxRequestsStale(true) is
      // the single source of truth for triggering a reload (see InboxView stale effect).
      setUnreadRequests(true);
      setInboxRequestsStale(true);
      if (isAgOpsHilRequestToast(extPayload)) {
        showHilRaisedToast({
          title: extPayload.title,
          description: extPayload.summary,
          hilId: extPayload.hilId,
          requestId: extPayload.requestId,
          agentId: extPayload.agentId,
          conversationId: extPayload.conversationId,
          taskId: extPayload.taskId,
        });
      }
    });

    // AGENT_STATUS_CHANGED — runtime status updates from the agent (e.g. run completed)
    const offAgentStatus = shellEventBus.on('AGENT_STATUS_CHANGED', e => {
      const ext = e.payload as {
        agentId: string;
        status: string;
        message?: string;
        summary?: string;
        conversationId?: string;
        responseType?: string;
        notificationId?: string;
        stagesCompleted?: number;
        stagesTotal?: number;
        stageLabel?: string;
      };
      const stagesCompleted =
        typeof ext.stagesCompleted === 'number' && ext.stagesCompleted > 0
          ? ext.stagesCompleted
          : undefined;
      const stagesTotal =
        typeof ext.stagesTotal === 'number' && ext.stagesTotal > 0 ? ext.stagesTotal : undefined;
      const backendTitle = (ext.message || '').trim();
      const backendSummary = (ext.summary || '').trim();
      const stageTitle =
        stagesCompleted != null && stagesTotal != null
          ? `Stage ${stagesCompleted} of ${stagesTotal} completed`
          : undefined;
      const inboxTitle = backendTitle || stageTitle || `Agent status: ${ext.status}`;
      const inboxBody =
        backendSummary ||
        (stageTitle && ext.stageLabel ? `${stageTitle} — ${ext.stageLabel}` : stageTitle) ||
        inboxTitle;
      addInboxNotification?.({
        id: `notif-status-${ext.agentId}-${ext.notificationId ?? Date.now()}`,
        urgency: 'normal',
        kind: 'info',
        title: inboxTitle,
        body: inboxBody,
        solution: '',
        time: new Date().toISOString(),
        read: false,
      });
      setUnreadNotifications(true);

      // Live-inject relay stage/plan updates into the chat thread cache.
      // Always cache by conversation_id so messages appear even if the active
      // thread briefly differs (rekey races); open the thread when none is active.
      const conversationId = (ext.conversationId || '').trim();
      const activeThreadId = useEsaDomainStore.getState().activeThreadId;
      const chatCopy = backendSummary || backendTitle || stageTitle;
      if (conversationId && chatCopy) {
        const responseType =
          ext.responseType ||
          (ext.status === 'FAILED'
            ? 'task_failed'
            : ext.status === 'SUCCEEDED'
              ? 'task_completed'
              : 'task_progress');
        const text = backendTitle || stageTitle || backendSummary;
        const description = backendSummary && backendSummary !== text ? backendSummary : undefined;
        const cache = getMessageCache();
        const existing = cache.getSnapshot(conversationId);
        const notifId = ext.notificationId || `relay-${ext.agentId}-${Date.now()}`;
        if (!existing.some(m => m.id === notifId)) {
          cache.addMessage(conversationId, {
            id: notifId,
            conversationId,
            parentMessageId: null,
            initiator: 'system',
            messageType: 'json',
            message: {
              text,
              ...(description ? { description } : {}),
              response_type: responseType,
              route: responseType,
              source: 'relay',
              task_id: ext.agentId || undefined,
              is_terminal: responseType === 'task_completed' || responseType === 'task_failed',
            },
            metadata: { response_type: responseType, task_id: ext.agentId },
            hasAttachments: false,
            storedFileUkey: null,
            createdAt: new Date().toISOString(),
          });
        }
        if (!activeThreadId || activeThreadId === conversationId) {
          if (!activeThreadId) {
            useEsaDomainStore.getState().setActiveThread(conversationId);
          }
        }
      }
    });

    return () => {
      offContext();
      offSurfaceSelection();
      offEsaContext();
      offOpen();
      offShellNavigation();
      offApproval();
      offAgentStatus();
    };
  }, [setSurfaceContext, setEsaPanelMode, addInboxNotification, addPendingHITLFn, setUnreadRequests, setUnreadNotifications, setInboxRequestsStale]);
}
